import React, { useEffect, useState } from 'react';
import PageHeader from '../components/PageHeader';
import LoadingState from '../components/LoadingState';
import ErrorState from '../components/ErrorState';
import { content } from '../../services/apiClient';

const DEFAULT_THEME = {
  theme_name: 'correfood',
  palette: {
    primary: '#FF6A00',
    page_bg: '#f8f9fa',
    card_bg: '#ffffff',
    footer_bg: '#202124',
    button_bg: '#FF6B00',
    button_text: '#ffffff',
    header_bg: '#ffffff'
  }
};

const DEFAULT_BANNER_SETTINGS = {
  interval_ms: 5000,
  transition: 'fade'
};

const DEFAULT_BANNER_CTA = {
  enabled: true,
  text: 'Ver ofertas',
  link: '/promocoes'
};

const getBannerId = () => (
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `banner-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
);

const createBanner = () => ({
  id: getBannerId(),
  image_url: '',
  title: '',
  subtitle: '',
  button_text: '',
  badge: ''
});

const resolveApiErrorMessage = (err, fallback = 'Falha ao enviar imagem') => {
  const detail = err?.response?.data?.detail;

  if (typeof detail === 'string' && detail.trim()) {
    return detail.trim();
  }

  if (Array.isArray(detail) && detail.length > 0) {
    const first = detail[0];
    if (typeof first === 'string' && first.trim()) {
      return first.trim();
    }
    if (first && typeof first === 'object') {
      const msg = first.msg || first.message || first.detail || first.error;
      if (typeof msg === 'string' && msg.trim()) {
        return msg.trim();
      }
      try {
        return JSON.stringify(first);
      } catch {
        return fallback;
      }
    }
  }

  if (detail && typeof detail === 'object') {
    const msg = detail.message || detail.detail || detail.error;
    if (typeof msg === 'string' && msg.trim()) {
      return msg.trim();
    }
    try {
      return JSON.stringify(detail);
    } catch {
      return fallback;
    }
  }

  const data = err?.response?.data;
  if (data && typeof data === 'object') {
    const msg = data.message || data.error;
    if (typeof msg === 'string' && msg.trim()) {
      return msg.trim();
    }
  }

  const message = typeof err?.message === 'string' ? err.message.trim() : '';
  if (message && message !== '[object Object]') {
    return message;
  }

  return fallback;
};

export default function AdminContentAppearance() {
  const [theme, setTheme] = useState(DEFAULT_THEME);
  const [banners, setBanners] = useState([createBanner()]);
  const [bannerSettings, setBannerSettings] = useState(DEFAULT_BANNER_SETTINGS);
  const [bannerCta, setBannerCta] = useState(DEFAULT_BANNER_CTA);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    let mounted = true;
    async function load() {
      setLoading(true);
      setError('');
      try {
        const [themeData, bannersData] = await Promise.all([
          content.getTheme(),
          content.getBanners()
        ]);
        if (mounted) {
          const mergedTheme = {
            ...DEFAULT_THEME,
            ...themeData,
            palette: { ...DEFAULT_THEME.palette, ...(themeData?.palette || {}) }
          };
          setTheme(mergedTheme);

          const items = Array.isArray(bannersData?.items) && bannersData.items.length > 0
            ? bannersData.items
            : [createBanner()];
          setBanners(items);

          const settings = bannersData?.settings || DEFAULT_BANNER_SETTINGS;
          setBannerSettings({
            interval_ms: Number(settings.interval_ms) || DEFAULT_BANNER_SETTINGS.interval_ms,
            transition: settings.transition || DEFAULT_BANNER_SETTINGS.transition
          });

          const cta = bannersData?.cta || DEFAULT_BANNER_CTA;
          setBannerCta({
            enabled: cta?.enabled !== false,
            text: typeof cta?.text === 'string' ? cta.text : DEFAULT_BANNER_CTA.text,
            link: typeof cta?.link === 'string' ? cta.link : DEFAULT_BANNER_CTA.link,
          });
        }
      } catch {
        if (mounted) setError('Erro ao carregar conteudo');
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => { mounted = false; };
  }, []);

  const handleThemeSave = async () => {
    await content.updateTheme(theme);
    alert('Tema atualizado');
  };

  const buildBannersPayload = () => {
    const validBanners = banners.filter((banner) => (banner.image_url || '').trim());
    return {
      payload: {
        items: validBanners,
        settings: bannerSettings,
        cta: {
          enabled: bannerCta?.enabled !== false,
          text: String(bannerCta?.text || '').trim() || null,
          link: String(bannerCta?.link || '').trim() || null,
        },
      },
      ignoredCount: Math.max(0, banners.length - validBanners.length),
    };
  };

  const handleBannerSave = async () => {
    const { payload, ignoredCount } = buildBannersPayload();
    await content.updateBanners(payload);
    const ignored = ignoredCount;
    if (ignored > 0) {
      alert(`Banners atualizados. ${ignored} banner(s) sem imagem foram ignorados.`);
      return;
    }
    alert('Banners atualizados');
  };

  const handleBannerCtaSave = async () => {
    const { payload } = buildBannersPayload();
    await content.updateBanners(payload);
    alert('Botao do banner atualizado');
  };

  const handleUpload = async (index, event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const response = await content.uploadBanner(file);
      const publicUrl = response?.data?.public_url || response?.data?.publicUrl;
      if (!publicUrl) {
        throw new Error('URL publica nao retornada');
      }
      setBanners((prev) => prev.map((banner, i) => i === index ? { ...banner, image_url: publicUrl } : banner));
    } catch (err) {
      console.error(err);
      const detail = resolveApiErrorMessage(err, 'Falha ao enviar imagem');
      alert(`Falha ao enviar imagem pelo backend: ${detail}`);
    } finally {
      setUploading(false);
    }
  };

  const updateBannerField = (index, field, value) => {
    setBanners((prev) => prev.map((banner, i) => i === index ? { ...banner, [field]: value } : banner));
  };

  const addBanner = () => {
    setBanners((prev) => [...prev, createBanner()]);
  };

  const removeBanner = (index) => {
    setBanners((prev) => prev.filter((_, i) => i !== index));
  };

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;

  return (
    <div className="admin-section">
      <PageHeader title="Conteudo e Aparencia" subtitle="Controle de banners e paleta do site" />

      <div className="admin-card">
        <h3>Banners da Home</h3>
        <p style={{ color: '#B8B8C5', marginBottom: 16 }}>Adicione quantos banners quiser e defina o estilo da transicao.</p>

        {banners.map((banner, index) => (
          <div key={banner.id || index} style={{ borderBottom: '1px solid #1F1F2A', paddingBottom: 16, marginBottom: 16 }}>
            <div className="admin-filters">
              <input
                className="admin-input"
                placeholder="URL da imagem"
                value={banner.image_url || ''}
                onChange={(e) => updateBannerField(index, 'image_url', e.target.value)}
              />
              <input
                className="admin-input"
                placeholder="Titulo"
                value={banner.title || ''}
                onChange={(e) => updateBannerField(index, 'title', e.target.value)}
              />
              <input
                className="admin-input"
                placeholder="Subtitulo"
                value={banner.subtitle || ''}
                onChange={(e) => updateBannerField(index, 'subtitle', e.target.value)}
              />
              <input
                className="admin-input"
                placeholder="Texto do botao"
                value={banner.button_text || ''}
                onChange={(e) => updateBannerField(index, 'button_text', e.target.value)}
              />
              <input
                className="admin-input"
                placeholder="Selo (ex: 20% OFF)"
                value={banner.badge || ''}
                onChange={(e) => updateBannerField(index, 'badge', e.target.value)}
              />
            </div>
            <div style={{ marginTop: 12, display: 'flex', gap: 12, alignItems: 'center' }}>
              <input type="file" onChange={(event) => handleUpload(index, event)} disabled={uploading} />
              <button className="admin-button secondary" type="button" onClick={() => removeBanner(index)} disabled={banners.length <= 1}>
                Remover
              </button>
            </div>
          </div>
        ))}

        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <button className="admin-button secondary" type="button" onClick={addBanner}>Adicionar banner</button>
          <button className="admin-button" type="button" onClick={handleBannerSave}>Salvar banners</button>
        </div>
      </div>

      <div className="admin-card">
        <h3>Botao sobre o banner</h3>
        <p style={{ color: '#B8B8C5', marginBottom: 16 }}>
          Defina se o botao aparece sobre os banners, o texto exibido e o link de redirecionamento.
        </p>
        <div className="admin-filters">
          <label style={{ color: '#B8B8C5', display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="checkbox"
              checked={bannerCta.enabled !== false}
              onChange={(e) => setBannerCta((prev) => ({ ...prev, enabled: e.target.checked }))}
            />
            Exibir botao nos banners
          </label>
          <input
            className="admin-input"
            placeholder="Texto do botao (opcional)"
            value={bannerCta.text || ''}
            onChange={(e) => setBannerCta((prev) => ({ ...prev, text: e.target.value }))}
          />
          <input
            className="admin-input"
            placeholder="Link do botao (opcional): /promocoes ou https://..."
            value={bannerCta.link || ''}
            onChange={(e) => setBannerCta((prev) => ({ ...prev, link: e.target.value }))}
          />
        </div>
        <p style={{ color: '#B8B8C5', marginTop: 8 }}>
          Se o link ficar vazio, o botao redireciona para /promocoes.
        </p>
        <button className="admin-button" type="button" onClick={handleBannerCtaSave} style={{ marginTop: 12 }}>
          Salvar botao do banner
        </button>
      </div>

      <div className="admin-card">
        <h3>Rotacao dos banners</h3>
        <div className="admin-filters">
          <input
            className="admin-input"
            type="number"
            min="2"
            max="60"
            value={Math.round((bannerSettings.interval_ms || 5000) / 1000)}
            onChange={(e) => setBannerSettings({
              ...bannerSettings,
              interval_ms: Number(e.target.value || 5) * 1000
            })}
          />
          <select
            className="admin-select"
            value={bannerSettings.transition || 'fade'}
            onChange={(e) => setBannerSettings({ ...bannerSettings, transition: e.target.value })}
          >
            <option value="slide">Entrar rolando</option>
            <option value="blink">Piscar</option>
            <option value="fade">Escurecer</option>
          </select>
        </div>
        <p style={{ color: '#B8B8C5', marginTop: 8 }}>Tempo em segundos e tipo de transicao entre banners.</p>
      </div>

      <div className="admin-card">
        <h3>Paleta do site</h3>
        <div className="admin-filters">
          <label style={{ color: '#B8B8C5' }}>Cor principal</label>
          <input type="color" value={theme.palette.primary || '#FF6A00'} onChange={(e) => setTheme({ ...theme, palette: { ...theme.palette, primary: e.target.value } })} />
          <label style={{ color: '#B8B8C5' }}>Fundo da pagina</label>
          <input type="color" value={theme.palette.page_bg || '#f8f9fa'} onChange={(e) => setTheme({ ...theme, palette: { ...theme.palette, page_bg: e.target.value } })} />
          <label style={{ color: '#B8B8C5' }}>Cards</label>
          <input type="color" value={theme.palette.card_bg || '#ffffff'} onChange={(e) => setTheme({ ...theme, palette: { ...theme.palette, card_bg: e.target.value } })} />
          <label style={{ color: '#B8B8C5' }}>Rodape</label>
          <input type="color" value={theme.palette.footer_bg || '#202124'} onChange={(e) => setTheme({ ...theme, palette: { ...theme.palette, footer_bg: e.target.value } })} />
          <label style={{ color: '#B8B8C5' }}>Botoes</label>
          <input type="color" value={theme.palette.button_bg || '#FF6B00'} onChange={(e) => setTheme({ ...theme, palette: { ...theme.palette, button_bg: e.target.value } })} />
          <label style={{ color: '#B8B8C5' }}>Texto do botao</label>
          <input type="color" value={theme.palette.button_text || '#ffffff'} onChange={(e) => setTheme({ ...theme, palette: { ...theme.palette, button_text: e.target.value } })} />
          <label style={{ color: '#B8B8C5' }}>Topo</label>
          <input type="color" value={theme.palette.header_bg || '#ffffff'} onChange={(e) => setTheme({ ...theme, palette: { ...theme.palette, header_bg: e.target.value } })} />
        </div>
        <button className="admin-button" onClick={handleThemeSave} style={{ marginTop: 12 }}>Salvar tema</button>
      </div>
    </div>
  );
}
